/**
 * Collections Carousel Runtime
 */
import $ from 'jquery';
import { initCarousel } from '../../_shared/carousel';
import { dispatchOceanSearch, getInitialCollectionSlug, getSearchParamsFromUrl, isOnSearchResultsPage, OceanSearchParams, OCEAN_SEARCH_EVENT } from '../../_shared/oceanSearchContext';

initCarousel('collections');

// "Visible" mode lays its cards out wider than the viewport on purpose (see
// collections.scss -- the design hangs 180px of the row off each edge at
// 1440), and the rest state is that overrun shared evenly between the two
// edges. A scroll container measures from its first card's left edge, so
// there's no layout property that says "centred": it has to be a starting
// scrollLeft, set once the track knows its own width.
//
// Mobile is excluded because it isn't that composition at all -- it's a
// one-card-at-a-time carousel that correctly starts on card one (its peek
// either side comes from centre snapping, again in the stylesheet).
// "Collapsed" shares that composition, so it's centred too -- but only once
// expanded: a collapsed track is capped at max-height 0, so it has no
// scrollWidth to divide and centring it on load is a no-op (see the toggle
// handler, which re-runs this on the way open).
const MOBILE_QUERY = '(max-width: 782px)'; // $ocean-mobile, scss/_thresh.scss
const CAROUSEL_SELECTOR = '.collections:not(.collections--featured) .collections__track';

const centreOverrunningTracks = (): void =>
	{
	if (window.matchMedia(MOBILE_QUERY).matches)
		{
		return;
		}

	$(CAROUSEL_SELECTOR).each((_i, element) =>
		{
		const track = element as HTMLElement;
		track.scrollLeft = (track.scrollWidth - track.clientWidth) / 2;
		});
	};

// How far a collapsed block runs up behind a booking bar sitting directly
// above it: to the bar's midline, so the bar floats on the panel rather than
// stacking on top of it (see --coll-overlap in collections.scss for the rest
// of the composition, and Figma 2002:1270 for the proportion).
//
// Measured here rather than written as a length in the stylesheet because the
// bar's height is content-driven -- two rows, each as tall as whatever
// controls the editor placed in them -- and no CSS selector can read a
// sibling's box. A block with no booking bar immediately before it (the
// journal listing) is left at the stylesheet's 0 default and doesn't overlap
// anything.
const BOOKING_BAR_SELECTOR = '.wp-block-ocean-booking-bar';

const setBookingBarOverlap = (): void =>
	{
	$('.collections--collapsed').each((_i, element) =>
		{
		const block = element as HTMLElement;
		const previous = block.previousElementSibling;

		if (!previous?.matches(BOOKING_BAR_SELECTOR))
			{
			return;
			}

		// Zeroed, not cleared, before measuring. Removing the inline values
		// would fall back to the stylesheet's first-frame estimate (102px on
		// desktop, see collections.scss), which is itself an overlap -- so the
		// measurement would be taken against an already-shifted panel and come
		// out short by that estimate, every run compounding the last. Setting
		// them to 0 puts the panel at its true un-overlapped resting place,
		// which is what the arithmetic below assumes.
		block.style.setProperty('--coll-overlap', '0px');
		block.style.setProperty('--coll-overlap-hidden', '0px');

		// The visible bar, not its block wrapper. The wrapper's box also
		// contains the bar's own 2rem block margins (booking-bar.scss gives it
		// display:flow-root so they can't escape it), and it's the painted bar
		// the panel has to line up against -- measuring the wrapper would put
		// the panel's edge on the midline of bar-plus-whitespace instead.
		//
		// Before the bar hydrates there is no .booking-bar yet, and the section
		// itself is the painted box: booking-bar.scss dresses the saved rows as
		// a skeleton of the finished bar, sized and styled on the section. So
		// the fallback isn't a degraded guess -- early it's the correct element
		// to measure, and the querySelector takes over once React has mounted.
		const barElement = (previous as HTMLElement).querySelector<HTMLElement>('.booking-bar') ?? (previous as HTMLElement);
		const bar = barElement.getBoundingClientRect();

		// Two distinct quantities, and the stylesheet needs them apart.
		//
		// The gap is dead space between the bar and this block, which simply
		// has to close before any overlap starts.
		//
		// The hidden half-bar is the part of the panel that ends up behind
		// the bar. Only this is worth padding back: it's height the panel has
		// but the reader can't see, so the prompt has to clear it to sit the
		// designed distance below the bar's bottom edge. Adding the gap back
		// too would push the prompt down by that much again.
		const gap = block.getBoundingClientRect().top - bar.bottom;
		const hidden = bar.height / 2;

		block.style.setProperty('--coll-overlap', `${gap + hidden}px`);
		block.style.setProperty('--coll-overlap-hidden', `${hidden}px`);
		});
	};

// Deliberately NOT called at parse time to beat first paint -- it can't. WP
// enqueues this as a viewScript with data-wp-strategy="defer", so the earliest
// this file runs is already after the document has parsed and painted. The
// first-frame position is the stylesheet's job instead (--coll-overlap's
// default in collections.scss, which the skeleton makes accurate); everything
// here is refinement on top of a page that already looks right.

// The /find-a-stay hero paragraphs carry these anchors so this block can
// swap in a collection's copy client-side (see page 1428). Their initial
// text is remembered as the "browsing" default to restore on re-expand.
const HEADER_TITLE_ID = 'find-a-stay-title';
const HEADER_SUBTITLE_ID = 'find-a-stay-subtitle';

let defaultTitle: string | null = null;
let defaultSubtitle: string | null = null;

const setHeaderCopy = (title: string, subtitle: string): void =>
	{
	const titleEl = document.getElementById(HEADER_TITLE_ID);
	const subtitleEl = document.getElementById(HEADER_SUBTITLE_ID);

	if (defaultTitle === null && titleEl)
		{
		defaultTitle = titleEl.textContent ?? '';
		}
	if (defaultSubtitle === null && subtitleEl)
		{
		defaultSubtitle = subtitleEl.textContent ?? '';
		}

	if (titleEl)
		{
		titleEl.textContent = title;
		}
	if (subtitleEl)
		{
		subtitleEl.textContent = subtitle;
		}
	};

const restoreHeaderCopy = (): void =>
	{
	if (defaultTitle !== null)
		{
		setHeaderCopy(defaultTitle, defaultSubtitle ?? '');
		}
	};

// Active-collection indicator (see collections.scss `.collections__card.is-active`).
const setActiveCard = (slug: string): void =>
	{
	$('.collections__card').removeClass('is-active');
	if (slug)
		{
		$(`.collections__card[data-slug="${CSS.escape(slug)}"]`).addClass('is-active');
		}
	};

// The row is wider than the viewport (see CAROUSEL_SELECTOR above), so a
// selected card is not necessarily on screen when the picker opens against
// an existing selection -- centring the overrun would be actively wrong
// there, since the point is to show which one is active.
// Run something once a just-expanded collapsed block has finished revealing
// its track. Anything that scrolls the track has to wait for this: while the
// max-height transition is still running the box is not scrollable yet, and
// a scrollLeft written during it is silently discarded.
//
// The timeout is a fallback, not a duration to tune -- transitionend does not
// fire at all if the block was already open (no transition to end) or if the
// user has reduced motion on.
const EXPAND_FALLBACK_MS = 500; // clears the 0.45s max-height transition

const whenExpanded = (block: HTMLElement | undefined, run: () => void): void =>
	{
	const track = block?.querySelector<HTMLElement>('.collections__track');
	if (!track)
		{
		return;
		}

	let done = false;
	const fire = (): void =>
		{
		if (done)
			{
			return;
			}
		done = true;
		track.removeEventListener('transitionend', onEnd);
		run();
		};

	const onEnd = (event: TransitionEvent): void =>
		{
		if (event.propertyName === 'max-height')
			{
			fire();
			}
		};

	track.addEventListener('transitionend', onEnd);
	window.setTimeout(fire, EXPAND_FALLBACK_MS);
	};

// Both callers add .is-expanded immediately before this, so it goes through
// whenExpanded for the same reason the toggle's centring call does.
const revealActiveCard = (slug: string): void =>
	{
	const card = document.querySelector<HTMLElement>(`.collections__card[data-slug="${CSS.escape(slug)}"]`);
	const block = card?.closest<HTMLElement>('.collections');
	whenExpanded(block ?? undefined, () =>
		{
		card?.scrollIntoView({ block: 'nearest', inline: 'center' });
		});
	};

// Both dispatchOceanSearch() calls below (a card click, and the collapsed-
// toggle's clear-on-close) already apply their own UI side effects inline
// -- but dispatchOceanSearch's CustomEvent dispatch is synchronous, so the
// OCEAN_SEARCH_EVENT listener further down would otherwise immediately
// re-process that same change a second time, right after (harmless --
// re-setting the same active card/header copy -- but redundant work, and
// for the toggle's clear-on-close specifically it would fight the user's
// own "hide the picker" click by re-expanding it in the same tick). Set
// before either call, consumed (and cleared) by the listener on the very
// next event -- CustomEvent dispatch has no async gap, so there's no race
// to worry about.
let ignoreNextSearchEvent = false;

document.addEventListener('DOMContentLoaded', () =>
	{
	centreOverrunningTracks();
	setBookingBarOverlap();

	// The bar's rows wrap as the viewport narrows (and restack entirely below
	// $ocean-mobile), so its height -- and with it the overlap -- is not a
	// load-time constant. ResizeObserver rather than a window resize listener:
	// the bar also changes height without the window doing anything, when a
	// field's selected value wraps a row.
	//
	// It watches the section, which survives hydration (React replaces its
	// contents, not the element), so the skeleton-to-real-bar swap registers
	// as a resize here and re-measures -- no separate hook for that moment.
	if (typeof ResizeObserver !== 'undefined')
		{
		const observer = new ResizeObserver(setBookingBarOverlap);
		$(BOOKING_BAR_SELECTOR).each((_i, bar) => observer.observe(bar));
		}

	// "Collapsed" mode: the toggle button reveals the carousel (see collections.scss
	// `.collections--collapsed:not(.is-expanded)`, which hides the nav/track by default).
	$('.collections__toggle').on('click', function ()
		{
		const $collections = $(this).closest('.collections');
		$collections.toggleClass('is-expanded');
		if ($collections.hasClass('is-expanded'))
			{
			restoreHeaderCopy();
			// Only now does the track have a width to measure -- collapsed it
			// sits at max-height 0, so the load-time centring above found
			// nothing to do. It has to wait for the reveal transition to
			// finish rather than a fixed number of frames: until the
			// max-height animation lands, the box is not yet scrollable and
			// the assignment is silently dropped.
			whenExpanded($collections[0], centreOverrunningTracks);
			}
		// Manually hiding the picker (a card pick no longer auto-collapses
		// it, below -- it stays open to keep showing the active selection)
		// -- treat this as backing out, clearing whatever was active rather
		// than keeping it silently applied with the picker hidden.
		else if (isOnSearchResultsPage())
			{
			const current = getSearchParamsFromUrl();
			if (current.collection)
				{
				restoreHeaderCopy();
				setActiveCard('');
				ignoreNextSearchEvent = true;
				dispatchOceanSearch({ ...current, collection: '' });
				}
			}
		});

	$('.collections__card').on('click', function (event)
		{
		const card = event.currentTarget as HTMLAnchorElement;
		const { slug, title, subtitle } = card.dataset;
		// Not a carousel card (featured-mode grid cards carry no data-slug),
		// or this block isn't sitting on the search-results page itself --
		// placed on any other page, the card's href is a real navigation
		// there (see render.php's ocean_get_collection_search_url()), so
		// leave the click alone.
		if (!slug || !isOnSearchResultsPage())
			{
			return;
			}

		event.preventDefault();
		setHeaderCopy(title ?? '', subtitle ?? '');
		setActiveCard(slug);

		ignoreNextSearchEvent = true;
		dispatchOceanSearch({ ...getSearchParamsFromUrl(), collection: slug });
		});

	// Another block on the page (ocean/booking-filters' Collections radio)
	// can change the collection filter directly -- keep this block's active
	// indicator/header copy in step, and if it's now scoped to a collection,
	// make sure a collapsed picker is showing it rather than left hidden
	// behind "View Collections" while a filter is silently active.
	document.addEventListener(OCEAN_SEARCH_EVENT, (event) =>
		{
		if (ignoreNextSearchEvent)
			{
			ignoreNextSearchEvent = false;
			return;
			}

		const detail = (event as CustomEvent<Partial<OceanSearchParams>>).detail || {};
		if (detail.collection === undefined)
			{
			return;
			}

		setActiveCard(detail.collection);

		if (detail.collection)
			{
			$('.collections--collapsed').addClass('is-expanded');
			const card = document.querySelector<HTMLElement>(`.collections__card[data-slug="${CSS.escape(detail.collection)}"]`);
			if (card)
				{
				setHeaderCopy(card.dataset.title ?? '', card.dataset.subtitle ?? '');
				}
			revealActiveCard(detail.collection);
			}
		else
			{
			restoreHeaderCopy();
			}
		});

	// Deep link / reload / back-forward: the URL (or, for a pretty-path
	// arrival from another page, window.oceanSearchData.initialCollection)
	// already carries the selection, so apply its copy without re-dispatching
	// a search -- and if we're landing already scoped to a collection, show
	// the picker open rather than collapsed behind "View Collections".
	const initialSlug = getInitialCollectionSlug();
	if (initialSlug)
		{
		setActiveCard(initialSlug);
		$('.collections--collapsed').addClass('is-expanded');

		const card = document.querySelector<HTMLElement>(`.collections__card[data-slug="${CSS.escape(initialSlug)}"]`);
		if (card)
			{
			setHeaderCopy(card.dataset.title ?? '', card.dataset.subtitle ?? '');
			}
		revealActiveCard(initialSlug);
		}
	});
